<template>
    <layout-card
        :isCustomizable="settings.isCustomizable"
        :loading="loading"
        :placeholder="placeholder"
        placeholderImg="/ressources/logged/img/dashboard/dashboard-calendar.svg"
        :error="error"
        @edit-card="toggleSettingsForm"
        @hide-card="$emit('hide-card', type)"
        @refresh-card="fetchCard(type, {}, true)"
        class="card-agenda"
    >
        <!-- SLOTED TITLE -->
        <template #title>
            {{ name }}
        </template>

        <!-- SLOTED ACTIONS -->
        <template #actions>
            <CompassIconButton
                icon="plus"
                onclick="Activities.openActivityForm(null, null, null, null, 'event')"
            />
            <portal to="dashboardSettingsForm">
                <card-setting-overlay
                    v-if="showSettingsForm"
                    :formName="settingsFormName"
                    :cardCustomSettings="customSettings"
                    :cardName="type"
                    :cardSettings="settings"
                    @fetched="fetchCard"
                    @close-form="toggleSettingsForm"
                    @custom-settings-saved="fetchCard(type, {}, true)"
                />
            </portal>
        </template>

        <!-- SLOTED CONTENT -->
        <template #content>
            <div
                v-if="(cardData && cardData.length > 0) || (cardData && cardData.error)"
                class="flex-column"
            >
                <div v-if="!cardData.error">
                    <div v-for="(eventsDay, index) in eventsByDays" :key="`${index}`">
                        <template v-if="eventsDay.length > 0">
                            <header>
                                <div class="grey ml-2 my-2 acenter line-behind">
                                    <span>
                                        {{
                                            $filters.dateFormatter(
                                                moment().add(index, 'days').unix(),
                                                'NORMAL_DATE',
                                                true,
                                            )
                                        }}
                                    </span>
                                </div>
                            </header>
                            <ul>
                                <li
                                    v-for="event in eventsDay"
                                    :key="event.id"
                                    class="event mb-1"
                                    v-on:click="eventDetail(event)"
                                    :style="{ backgroundColor: calendarColor(event) }"
                                >
                                    <div
                                        :style="{ borderColor: labelColor(event) }"
                                        :class="{ 'gray-60': isLate(event.end) }"
                                    >
                                        <!-- Details -->
                                        <div class="details">
                                            <div>
                                                <p class="lab gray-60">{{ event.label }}</p>
                                                <p class="title">{{ event.title }}</p>
                                            </div>
                                        </div>
                                        <!-- Related link -->
                                        <p v-if="event.relateds" class="ml-2 mt-1">
                                            <a
                                                v-if="event.relateds[0]"
                                                class="link regular gray-60 fs-2"
                                                :href="event.relateds[0].relatedlink"
                                                v-on:click.stop=""
                                            >
                                                {{ event.relateds[0].relatedname }}
                                            </a>
                                            <a
                                                v-if="event.relateds[1]"
                                                class="link regular gray-60 fs-2 ml-3"
                                                :href="event.relateds[1].relatedlink"
                                                v-on:click.stop=""
                                            >
                                                {{ event.relateds[1].relatedname }}
                                            </a>
                                        </p>
                                        <!-- Date -->
                                        <div class="time">
                                            <span class="gray-60">{{ dateCase(event) }}</span>
                                        </div>
                                    </div>
                                </li>
                            </ul>
                        </template>
                    </div>
                    <a href="/?_f=agenda" class="link acenter dd mt-4">{{
                        $l10n.__('Voir tous les événements')
                    }}</a>
                </div>
                <div v-else>
                    <div class="alert alert-danger">{{ cardData.error }}</div>
                </div>
            </div>
        </template>
    </layout-card>
</template>

<script>
import LayoutCard from './LayoutCard.vue';
import CardMixin from './CardMixin.js';
import hexToRgba from 'hex-to-rgba';
import { createNamespacedHelpers } from 'vuex';

const corpMapState = createNamespacedHelpers('corp').mapState;
const userMapState = createNamespacedHelpers('user').mapState;

export default {
    mixins: [CardMixin],
    data() {
        return {
            settingsFormName: 'agenda-setting-form',
        };
    },
    created() {
        window.EventBus.on('refresh_event', () => {
            this.fetchCard(this.type);
        });
    },
    methods: {
        moment() {
            return moment();
        },
        keepTime(date) {
            let format = this.hourformat === '24' ? 'HH:mm' : 'hh:mm A';
            return moment(date, 'X').tz(this.timezone).format(format);
        },
        eventDetail(event) {
            Agenda.preview(
                event.id,
                event.calendar,
                '',
                moment(event.start, 'X').tz(this.timezone).format('YYYY-MM-DD HH:mm:ss'),
                moment(event.end, 'X').tz(this.timezone).format('YYYY-MM-DD HH:mm:ss'),
                event.isRecurring,
                event.title,
            );
        },
        labelColor(event) {
            return '#' + event.labelColor;
        },
        calendarColor(event) {
            if (this.settings.provider === 'sellsy' || this.customSettings.calendars.length == 1) {
                return 'white';
            } else {
                let calendarEvent = this.settings.calendars.find(
                    (calendar) => calendar.id === event.calendar,
                );
                return calendarEvent.backgroundColor
                    ? hexToRgba(calendarEvent.backgroundColor, 0.15)
                    : 'white';
            }
        },
        isLate(endTime) {
            return moment().tz(this.timezone).unix() > endTime ? true : false;
        },
        isOneDay(event) {
            return moment(event.start, 'X').isSame(moment(event.end, 'X'), 'day');
        },
        dateCase(event) {
            if (event.allDay) {
                return this.$l10n.__('Toute la journée'); // All day case
            } else if (this.isOneDay(event)) {
                return this.keepTime(event.start) + ' - ' + this.keepTime(event.end); // Regular case
            } else if (event.startToday !== false) {
                return this.keepTime(event.start) + ' - ...'; // multiple day start case
            } else if (event.endToday !== false) {
                return '... - ' + this.keepTime(event.end); // multiple day end case
            } else {
                return this.$l10n.__('Toute la journée'); // multiple day between start and end case
            }
        },
    },
    computed: {
        ...userMapState(['timezone']),

        ...corpMapState(['hourformat']),

        eventsByDays() {
            var orderedEvents = Array.isArray(this.cardData) ? this.cardData : [];
            var eventsByDays = [];

            if (this.customSettings.displayMode === 'days') {
                var nbDays = this.customSettings.nbDays;
            } else {
                //take nb days between today and last events
                let lastEvent = orderedEvents[orderedEvents.length - 1];
                var nbDays = moment(lastEvent.end, 'X')
                    .endOf('day')
                    .diff(moment().startOf('day'), 'days');
            }

            //generate daysok arrays
            for (var i = 0; i <= nbDays; i++) {
                eventsByDays.push([]);
            }

            _.each(orderedEvents, (event) => {
                //for outlook event : Because event during allDay have a start and end date to midnight, timezone can cause mismatch on date
                //(eg for Paris timezone: event during all day will be display at 02:00 to 02:00 on next day)
                if (event.allDay && this.settings.provider === 'outlook') {
                    var startDayEvent = moment(event.start, 'X').tz('UTC').startOf('day');
                    var endDayEvent = moment(event.end, 'X').tz('UTC').endOf('day');
                } else {
                    var startDayEvent = moment(event.start, 'X').tz(this.timezone).startOf('day');
                    var endDayEvent = moment(event.end, 'X').tz(this.timezone).endOf('day');
                }

                for (var day = 0; day <= nbDays; day++) {
                    if (
                        moment()
                            .tz(this.timezone)
                            .add(day, 'd')
                            .isBetween(startDayEvent, endDayEvent, null, '[]')
                    ) {
                        //If yes, check start == today
                        if (
                            moment(event.start, 'X')
                                .tz(this.timezone)
                                .isSame(moment().tz(this.timezone).add(day, 'd'), 'day')
                        ) {
                            event.startToday = moment(event.start, 'X').tz(this.timezone);
                        } else {
                            event.startToday = false;
                        }
                        // Again for end
                        if (
                            moment(event.end, 'X')
                                .tz(this.timezone)
                                .isSame(moment().tz(this.timezone).add(day, 'd'), 'day')
                        ) {
                            event.endToday = moment(event.end, 'X').tz(this.timezone);
                        } else {
                            event.endToday = false;
                        }
                        eventsByDays[day].push(Object.assign({}, event));
                    }
                }
            });

            return eventsByDays;
        },
        placeholder() {
            if (this.customSettings) {
                switch (this.customSettings.displayMode) {
                    case 'days':
                        return this.$l10n.n__(
                            "Aucun événement aujourd'hui",
                            'Aucun événement dans les %(nb)d prochains jours',
                            this.customSettings.nbDays,
                            { nb: this.customSettings.nbDays },
                        );
                    case 'events':
                        return this.$l10n.__("Vous n'avez aucun événement à venir");
                }
            }
        },
    },
    components: {
        LayoutCard,
    },
};
</script>

<style lang="scss" scoped>
@import '~@sellsy/styleguide/src/_variables';
@import '~@sellsy/styleguide/src/_mixins';

ul {
    .event {
        align-items: center;
        padding: spacer(1) spacer(2);
        @include border-radius();
        cursor: pointer;

        &:hover {
            background-color: $primary-05 !important;
        }

        > div {
            border-left: $gray-60 $val-1 solid;
            > div {
                display: flex;

                &.time {
                    flex-shrink: 0;
                    padding: spacer(1) spacer(2);
                    font-size: $small-font-size;
                    text-align: center;

                    .endTime {
                        color: $gray-60;
                    }
                }

                &.details {
                    min-height: 40px;
                    display: table;
                    padding-left: spacer(2);
                    font-size: $small-font-size;

                    .lab {
                        padding: spacer(1) 0;
                    }
                    .title {
                        font-size: $font-size;
                        font-weight: $font-weight-semibold;
                    }

                    > div {
                        display: table-cell;
                        vertical-align: middle;
                    }
                }
            }
        }
    }
}
</style>
